import { lazy, Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { NotFoundPage } from './pages/NotFoundPage';
import { ErrorBoundary } from './components/ErrorBoundary';
import { Toaster } from 'sonner';
import { ToastProvider, AnimatedPage } from '@theonefamily/ui';
import { useAuth } from './contexts/AuthContext';
import { MspShell } from './components/layout/MspShell';

// Dashboard
const MspDashboardPage = lazy(() =>
  import('./modules/dashboard/pages/MspDashboardPage').then(m => ({ default: m.MspDashboardPage })),
);

// Contract Analysis
const ContractAnalysisPage = lazy(() =>
  import('./modules/contracts/pages/ContractAnalysisPage').then(m => ({ default: m.ContractAnalysisPage })),
);
const ContractUploadPage = lazy(() =>
  import('./modules/contracts/pages/ContractUploadPage').then(m => ({ default: m.ContractUploadPage })),
);
const ContractDetailPage = lazy(() =>
  import('./modules/contracts/pages/ContractDetailPage').then(m => ({ default: m.ContractDetailPage })),
);

// Legal Health
const LegalHealthPage = lazy(() =>
  import('./modules/health/pages/LegalHealthPage').then(m => ({ default: m.LegalHealthPage })),
);

// Policies
const PolicyTemplateBrowserPage = lazy(() =>
  import('./modules/policies/pages/PolicyTemplateBrowserPage').then(m => ({ default: m.PolicyTemplateBrowserPage })),
);
const PolicyCustomizeWizardPage = lazy(() =>
  import('./modules/policies/pages/PolicyCustomizeWizardPage').then(m => ({ default: m.PolicyCustomizeWizardPage })),
);
const DeployedPoliciesPage = lazy(() =>
  import('./modules/policies/pages/DeployedPoliciesPage').then(m => ({ default: m.DeployedPoliciesPage })),
);
const PolicyDetailPage = lazy(() =>
  import('./modules/policies/pages/PolicyDetailPage').then(m => ({ default: m.PolicyDetailPage })),
);
const PolicyCompliancePage = lazy(() =>
  import('./modules/policies/pages/PolicyCompliancePage').then(m => ({ default: m.PolicyCompliancePage })),
);

// eDiscovery
const LegalHoldsPage = lazy(() =>
  import('./modules/ediscovery/pages/LegalHoldsPage').then(m => ({ default: m.LegalHoldsPage })),
);
const CollectionsPage = lazy(() =>
  import('./modules/ediscovery/pages/CollectionsPage').then(m => ({ default: m.CollectionsPage })),
);
const ReviewPage = lazy(() =>
  import('./modules/ediscovery/pages/ReviewPage').then(m => ({ default: m.ReviewPage })),
);
const ExportsPage = lazy(() =>
  import('./modules/ediscovery/pages/ExportsPage').then(m => ({ default: m.ExportsPage })),
);

// Settings
const SettingsPage = lazy(() =>
  import('./modules/settings/pages/SettingsPage').then(m => ({ default: m.SettingsPage })),
);

function Spinner() {
  return (
    <div className="flex h-64 items-center justify-center">
      <div className="h-6 w-6 animate-spin rounded-full border-4 border-primary border-t-transparent" />
    </div>
  );
}

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      </div>
    );
  }

  // If not authenticated, AuthContext will redirect to Hub for SSO
  if (!isAuthenticated) return null;

  return <>{children}</>;
}

export function App() {
  return (
    <ErrorBoundary>
    <ToastProvider>
      <AnimatedPage>
        <Routes>
          <Route
            path="/*"
            element={
              <ProtectedRoute>
                <MspShell>
                  <Suspense fallback={<Spinner />}>
                    <Routes>
                      <Route path="/" element={<Navigate to="/dashboard" replace />} />
                      <Route path="/dashboard" element={<MspDashboardPage />} />
                      {/* Contract Analysis */}
                      <Route path="/contracts" element={<ContractAnalysisPage />} />
                      <Route path="/contracts/new" element={<ContractUploadPage />} />
                      <Route path="/contracts/:analysisId" element={<ContractDetailPage />} />
                      {/* Legal Health */}
                      <Route path="/legal-health" element={<LegalHealthPage />} />
                      {/* Policies */}
                      <Route path="/policies/templates" element={<PolicyTemplateBrowserPage />} />
                      <Route path="/policies/templates/:templateId/customize" element={<PolicyCustomizeWizardPage />} />
                      <Route path="/policies" element={<DeployedPoliciesPage />} />
                      <Route path="/policies/:policyId" element={<PolicyDetailPage />} />
                      <Route path="/policies/compliance" element={<PolicyCompliancePage />} />
                      {/* eDiscovery */}
                      <Route path="/ediscovery/holds" element={<LegalHoldsPage />} />
                      <Route path="/ediscovery/collections" element={<CollectionsPage />} />
                      <Route path="/ediscovery/review" element={<ReviewPage />} />
                      <Route path="/ediscovery/exports" element={<ExportsPage />} />
                      {/* Settings */}
                      <Route path="/settings" element={<SettingsPage />} />
                      <Route path="*" element={<NotFoundPage />} />
                    </Routes>
                  </Suspense>
                </MspShell>
              </ProtectedRoute>
            }
          />
        </Routes>
      </AnimatedPage>
      <Toaster position="top-right" richColors />
    </ToastProvider>
    </ErrorBoundary>
  );
}
